import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import ErrorBoundary from 'js/components/ErrorBoundary'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import cx from 'js/lib/helpers/cx'

import styles from './SuspenseBoundary.module.css'
import type { SuspenseBoundaryProps } from './SuspenseBoundary.types'

/**
 * Suspense with a loading and an error state in the same reserved space,
 * so a lazy component neither shifts the layout nor takes the page down when its chunk fails.
 */
export const SuspenseBoundary = ({
    children,
    aspectRatio,
    width,
    errorFallback,
}: SuspenseBoundaryProps) => {
    const { t } = useTranslation()

    const style = { aspectRatio, width }

    return (
        <ErrorBoundary
            fallback={
                <div
                    role="alert"
                    className={cx(styles.placeholder, styles.error)}
                    style={style}
                >
                    {errorFallback ?? t('error.error-boundary-fallback')}
                </div>
            }
        >
            <Suspense
                fallback={
                    <div aria-busy className={styles.placeholder} style={style}>
                        <LoadingSpinner />
                    </div>
                }
            >
                {children}
            </Suspense>
        </ErrorBoundary>
    )
}
